import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from '../axios';
import compressImage from '../../lib/image_compressor';
import ModalConfirm from '../modal/hooks/modal_comfirm';
import { requestPrecheck } from '../../lib/talkroom_ai_precheck';

// ⚠️ このコンポーネントは現状レンダリングされない。userfeed_reply_list.js:307 の
//   `{hashedCompanyId && …}` が常に false になるため（マウント元の show.html.erb が
//   hashedCompanyId を渡していない）。TGW-2311 の送信前 AI 警告は、フォームが復活したときに
//   無警告の投稿経路にならないよう先に配線してある（docs/talkroom_ai_warning_spec.md §13）。
const UserfeedSendForm = ({
  userfeedId,
  loadNewReply,
  selectedReply,
  onPressCloseButton,
}) => {
  const [image, setImage] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const initialRows = 1;
  const maxRows = 5;
  const [rows, setRows] = useState(initialRows);
  const warningModalRef = useRef(null);
  const [warning, setWarning] = useState(null);

  const postReply = (precheckId) => {
    const formData = new FormData();
    if (reply) formData.append('reply', reply);
    if (image) formData.append('image', image);
    if (selectedReply)
      formData.append('parent_userfeeds_reply_id', selectedReply.id);
    // 警告の有無にかかわらず、判定できていれば必ず付ける（§13 契約表 10）。
    // warn で分岐させると「警告が出なかった投稿がどれだけ投稿されたか」の分母が取れなくなる。
    if (precheckId != null) formData.append('ai_precheck_id', precheckId);

    return axios
      .post(`/api/userfeeds/${userfeedId}/replies`, formData)
      .then(() => {
        loadNewReply();
        setReply('');
        setImage(null);
        onPressCloseButton();
      })
      .catch((error) => {
        // catch が無いと、送信に失敗したとき loading が戻らず入力欄が固まったままになり、
        // ユーザーは警告に従って書き直すことも再送することもできなくなる。
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;
    if (reply === '' && image === null) return;

    // loading は precheck の開始から送信完了（またはモーダルで「見直す」を選んだ時点）まで
    // 通して立てる。ここが抜けると確認モーダルが開いている間も送信ボタンを押せてしまい、
    // モーダルの選択を待たずに送信できる ＝ 警告機能が素通しになる（§13）。
    setLoading(true);

    // 本文を書いていない送信（画像のみ）は判定する対象が無いので呼ばない（§13 契約表 9）。
    if (reply === '') {
      await postReply(null);
      return;
    }

    const result = await requestPrecheck({ body: reply, userfeedId });
    if (!result.warn) {
      await postReply(result.precheckId);
      return;
    }

    setWarning(result);
    if (warningModalRef.current) warningModalRef.current.openModal();
  };

  const handleWarningApprove = () => {
    const precheckId = warning ? warning.precheckId : null;
    setWarning(null);
    postReply(precheckId);
  };

  // 「見直す」は投稿 API を呼ばず、入力内容を保持したまま編集に戻る（§13 契約表 11）。
  const handleWarningReject = () => {
    setWarning(null);
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey && !e.metaKey) || (!e.ctrlKey && e.metaKey)) {
      if (e.keyCode === 13) {
        handleSubmit(e);
        return false;
      }
    }
  };

  const handleChange = useCallback((e) => {
    const value = e.currentTarget.value;
    setReply(value);
  }, []);

  useEffect(() => {
    const rows = reply.split('\n').length;
    setRows(
      rows > initialRows ? (rows > maxRows ? maxRows : rows) : initialRows
    );
  }, [reply]);

  return (
    <div className="fixed-bottom bg-graydark">
      <ModalConfirm
        ref={warningModalRef}
        title={warning ? warning.title : ''}
        content={warning ? warning.message : ''}
        rejectButtonName="見直す"
        approveButtonName="このまま送信"
        rejectFunc={handleWarningReject}
        approveFunc={handleWarningApprove}
      />
      {selectedReply && (
        <div className="container col-md-10 col-lg-8 my-2">
          <div className="d-flex justify-content-center align-items-center">
            <div>
              <img
                data-src={selectedReply.user && selectedReply.user.avatar_thumb}
                className="lazyload rounded-circle"
                alt="reply-user-image"
                width={40}
              />
            </div>
            <div className="flex-fill px-3">
              <p className="text-fz-xs text-black-20 mb-1 line-1 p-0">
                @{selectedReply.user && selectedReply.user.tunagate_id}
              </p>
              {selectedReply.image_url_medium ? (
                <div>
                  {selectedReply.reply && (
                    <p className="m-0 text-fz-12px">{selectedReply.reply}</p>
                  )}
                  <div className="message-photo">
                    <img
                      src={selectedReply.image_url_medium}
                      style={{ width: '100%', maxWidth: '100px' }}
                    />
                  </div>
                </div>
              ) : (
                <p className="m-0 text-fz-12px">{selectedReply.reply}</p>
              )}
            </div>
            <div>
              <div
                onClick={() => onPressCloseButton()}
                className="text-right"
                style={{ width: '20px' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512">
                  <path d="M242.7 256l100.1-100.1c12.3-12.3 12.3-32.2 0-44.5l-22.2-22.2c-12.3-12.3-32.2-12.3-44.5 0L176 189.3 75.9 89.2c-12.3-12.3-32.2-12.3-44.5 0L9.2 111.5c-12.3 12.3-12.3 32.2 0 44.5L109.3 256 9.2 356.1c-12.3 12.3-12.3 32.2 0 44.5l22.2 22.2c12.3 12.3 32.2 12.3 44.5 0L176 322.7l100.1 100.1c12.3 12.3 32.2 12.3 44.5 0l22.2-22.2c12.3-12.3 12.3-32.2 0-44.5L242.7 256z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="container col-md-10 col-lg-8 pr-0">
        <div className="input-group pt-2 pb-2">
          <div className="input-group-append p-0">
            <label htmlFor="message-photo" className="mt-1 pr-2">
              <img
                src="/assets2019/img/icon_camera.svg"
                alt=""
                className="icon--camera"
              />
              <input
                type="file"
                id="message-photo"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const compressed = await compressImage(file);
                  setImage(compressed);
                }}
              />
            </label>
          </div>
          <textarea
            className="form-control border-0 rounded"
            rows={rows}
            id="ta-message"
            placeholder="ここに返信を入力してください。"
            onChange={handleChange}
            value={reply}
            onKeyDown={(e) => handleKeyDown(e)}
            style={{ resize: 'none' }}
          />{' '}
          <div className="input-group-append">
            <button
              id="btnMessageSubmit"
              className="btn text-black-50 text-fw-bold"
              onClick={(e) => {
                loading ? null : handleSubmit(e);
              }}
              disabled={loading}
            >
              {loading ? '送信中...' : '送信'}
            </button>
          </div>
        </div>
        {image && (
          <div className="d-flex justify-content-start">
            <img
              src={URL.createObjectURL(image)}
              alt="Preview Image"
              className="icon--camera mb-1"
              style={{ width: 40, height: 40 }}
            />
            <button
              className="btn text-black-50 mb-1"
              onClick={() => setImage(null)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                className="icon--camera"
                style={{ width: 20, height: 20 }}
              >
                <path d="M376.6 84.5c11.3-13.6 9.5-33.8-4.1-45.1s-33.8-9.5-45.1 4.1L192 206 56.6 43.5C45.3 29.9 25.1 28.1 11.5 39.4S-3.9 70.9 7.4 84.5L150.3 256 7.4 427.5c-11.3 13.6-9.5 33.8 4.1 45.1s33.8 9.5 45.1-4.1L192 306 327.4 468.5c11.3 13.6 31.5 15.4 45.1 4.1s15.4-31.5 4.1-45.1L233.7 256 376.6 84.5z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserfeedSendForm;
